import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import {
  Radar,
  RefreshCw,
  Clock,
  Zap,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ExternalLink,
  ShoppingCart,
  Settings2,
} from 'lucide-react';

import { getApiUrl, getAuthenticatedAxios } from '../../utils/api';

const API_URL = getApiUrl();

const REFRESH_MS = 5000;

const BlumOnChainScanner = () => {
  const api = getAuthenticatedAxios();

  // Scanner state
  const [stats, setStats] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [intervalInput, setIntervalInput] = useState('');
  const [savingInterval, setSavingInterval] = useState(false);

  // Wallets state
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(true);

  // Buy form state
  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedWalletIds, setSelectedWalletIds] = useState(new Set());
  const [amountTon, setAmountTon] = useState('1');
  const [slippage, setSlippage] = useState('3');
  const [buying, setBuying] = useState(false);
  const [lastBuyResult, setLastBuyResult] = useState(null);

  // --- Fetchers

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get(`${API_URL}/api/blum/deploy-scanner/stats`);
      setStats(r.data);
      if (!intervalInput && r.data?.poll_interval_seconds) {
        setIntervalInput(String(r.data.poll_interval_seconds));
      }
    } catch (e) {
      // silent
    }
  }, [api, intervalInput]);

  const loadTokens = useCallback(async () => {
    try {
      const r = await api.get(`${API_URL}/api/blum/deploy-scanner/tokens?limit=100`);
      setTokens(r.data?.tokens || []);
    } catch (e) {
      // silent
    } finally {
      setTokensLoading(false);
    }
  }, [api]);

  const loadWallets = useCallback(async () => {
    try {
      const r = await api.get(`${API_URL}/api/wallets/list?limit=1000`);
      const list = r.data?.wallets || [];
      setWallets(list);
    } catch (e) {
      toast.error('Ошибка загрузки кошельков');
    } finally {
      setWalletsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadStats();
    loadTokens();
    loadWallets();
    const t = setInterval(() => {
      loadStats();
      loadTokens();
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  // --- Actions

  const saveInterval = async () => {
    const sec = parseInt(intervalInput, 10);
    if (!sec || sec < 3 || sec > 3600) {
      toast.error('Интервал должен быть от 3 до 3600 секунд');
      return;
    }
    setSavingInterval(true);
    try {
      await api.post(`${API_URL}/api/blum/deploy-scanner/config`, {
        poll_interval_seconds: sec,
      });
      toast.success(`Интервал обновлён: ${sec}с`);
      await loadStats();
    } catch (e) {
      toast.error('Ошибка обновления интервала');
    } finally {
      setSavingInterval(false);
    }
  };

  const forceRun = async () => {
    try {
      toast.info('Запускаю скан...');
      const r = await api.post(`${API_URL}/api/blum/deploy-scanner/run-once`);
      const d = r.data || {};
      toast.success(
        `Готово: actions=${d.actions_seen ?? 0}, deploys=${d.deploys_processed ?? 0}`,
      );
      await Promise.all([loadStats(), loadTokens()]);
    } catch (e) {
      toast.error('Ошибка ручного скана');
    }
  };

  const toggleWallet = (id) => {
    setSelectedWalletIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllWalletsWithBalance = () => {
    const min = parseFloat(amountTon) || 1;
    const ids = wallets
      .filter((w) => (parseFloat(w.ton_balance || 0)) >= min + 0.15)
      .map((w) => w.address || w.wallet_id || w.id);
    setSelectedWalletIds(new Set(ids));
  };

  const executeBuy = async () => {
    if (!selectedToken) {
      toast.error('Выбери токен');
      return;
    }
    if (selectedWalletIds.size === 0) {
      toast.error('Выбери хотя бы один кошелёк');
      return;
    }
    const amt = parseFloat(amountTon);
    if (!amt || amt < 1) {
      toast.error('Минимальная сумма — 1 TON');
      return;
    }
    const slip = parseFloat(slippage);
    if (!slip || slip <= 0 || slip > 50) {
      toast.error('Slippage: 0.3 – 50%');
      return;
    }
    setBuying(true);
    setLastBuyResult(null);
    try {
      const r = await api.post(`${API_URL}/api/blum/trade/group-buy`, {
        token_address: selectedToken.master_address,
        wallet_ids: Array.from(selectedWalletIds),
        amount_ton: amt,
        slippage_percent: slip,
        delay_min_ms: 500,
        delay_max_ms: 2000,
      });
      const d = r.data || {};
      setLastBuyResult(d);
      const ok = d.successful_buys ?? 0;
      const bad = d.failed_buys ?? 0;
      if (ok > 0 && bad === 0) {
        toast.success(`✅ Куплено ${ok}/${d.total_wallets} успешно`);
      } else if (ok > 0 && bad > 0) {
        toast(`⚠️ Успех ${ok}, ошибок ${bad}`, { description: 'смотри детали ниже' });
      } else {
        toast.error(`❌ Все ${bad} попыток упали — смотри ошибки ниже`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setLastBuyResult({ success: false, errors: [{ error: msg }] });
      toast.error(`Ошибка покупки: ${msg}`);
    } finally {
      setBuying(false);
    }
  };

  // --- Derived

  const walletsWithBalance = useMemo(() => {
    // ton_balance приходит из backend в наноTON (string). Переводим в TON.
    const NANO = 1e9;
    return wallets
      .map((w) => {
        const raw = w.ton_balance;
        const nano = typeof raw === 'string' ? parseFloat(raw) : Number(raw || 0);
        const ton = isFinite(nano) ? nano / NANO : 0;
        return {
          ...w,
          _id: w.address || w.wallet_id || w.id,
          _balance: ton,
        };
      })
      .filter((w) => w._balance > 0.05)
      .sort((a, b) => b._balance - a._balance);
  }, [wallets]);

  const fmtTs = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('ru-RU');
    } catch {
      return iso.slice(0, 19);
    }
  };

  const shortAddr = (a) => (a ? `${a.slice(0, 8)}…${a.slice(-6)}` : '—');

  return (
    <div className="space-y-6">
      {/* Stats + Settings */}
      <Card className="bg-white/5 border-white/10 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Radar className="h-5 w-5 text-purple-400" />
            On-Chain Scanner
          </CardTitle>
          <CardDescription className="text-purple-200">
            Автоматически находит новые Blum BCL-токены прямо из блокчейна (без Blum API / JWT).
            Источник: Toncenter <code className="text-xs bg-white/10 px-1 py-0.5 rounded">/actions</code> на
            контроллер <code className="text-xs bg-white/10 px-1 py-0.5 rounded">EQBdyhU…</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatBox label="Статус" value={stats?.running ? 'работает' : 'остановлен'}
                     ok={stats?.running} />
            <StatBox label="Найдено токенов" value={tokens.length} />
            <StatBox label="Деплоев обработано" value={stats?.deploys_seen ?? 0} />
            <StatBox label="Ошибок" value={stats?.errors ?? 0}
                     ok={(stats?.errors ?? 0) === 0} />
          </div>

          <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-white/10">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-purple-200 flex items-center gap-1">
                <Settings2 className="h-3 w-3" /> Интервал сканирования (сек, 3–3600)
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="3"
                  max="3600"
                  value={intervalInput}
                  onChange={(e) => setIntervalInput(e.target.value)}
                  className="w-24 bg-white/10 border-white/20 text-white"
                />
                <Button onClick={saveInterval} disabled={savingInterval}
                        className="bg-purple-600 hover:bg-purple-700">
                  {savingInterval ? <Loader2 className="h-4 w-4 animate-spin"/> : 'Сохранить'}
                </Button>
              </div>
            </div>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={forceRun}
                      className="border-white/20 text-white hover:bg-white/10">
                <Zap className="h-4 w-4 mr-2"/> Запустить сейчас
              </Button>
              <Button variant="outline" onClick={() => { loadTokens(); loadStats(); }}
                      className="border-white/20 text-white hover:bg-white/10">
                <RefreshCw className="h-4 w-4 mr-2"/> Обновить
              </Button>
            </div>
          </div>
          {stats?.last_ok_at && (
            <div className="mt-3 text-xs text-purple-300 flex items-center gap-2">
              <Clock className="h-3 w-3"/>
              Последний sync: {fmtTs(stats.last_ok_at)} (контроллер pos: {stats.last_utime})
            </div>
          )}
          {stats?.last_error && (
            <div className="mt-1 text-xs text-red-300 flex items-center gap-2">
              <AlertCircle className="h-3 w-3"/> {stats.last_error}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tokens */}
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Найденные токены ({tokens.length})</CardTitle>
            <CardDescription className="text-purple-200">
              Самые новые сверху. Клик — выбрать для покупки.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tokensLoading ? (
              <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin text-purple-400 mx-auto"/></div>
            ) : tokens.length === 0 ? (
              <div className="text-center py-8 text-purple-200/70">Пока ничего не найдено. Сканер проверяет контроллер каждые {stats?.poll_interval_seconds ?? '?'}с.</div>
            ) : (
              <div className="space-y-2 max-h-[540px] overflow-y-auto pr-1">
                {tokens.map((t) => {
                  const picked = selectedToken?.master_address === t.master_address;
                  return (
                    <div
                      key={t.master_address}
                      onClick={() => setSelectedToken(t)}
                      className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors border ${
                        picked
                          ? 'bg-purple-500/20 border-purple-400'
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-purple-500/40 flex items-center justify-center text-xs font-bold text-white">
                          {(t.symbol || '?').charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-white font-medium truncate">
                            {t.symbol || 'UNKNOWN'}{' '}
                            <span className="text-purple-300 text-xs">— {t.name || '—'}</span>
                          </div>
                          <div className="text-xs text-purple-300/80">
                            {fmtTs(t.created_at || t.discovered_at)} · {shortAddr(t.master_address)}
                            <a
                              href={`https://tonviewer.com/${t.master_address}`}
                              target="_blank" rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="ml-2 inline-flex items-center text-purple-200 hover:text-white"
                            >
                              <ExternalLink className="h-3 w-3"/>
                            </a>
                          </div>
                        </div>
                      </div>
                      {picked && <CheckCircle2 className="h-5 w-5 text-purple-300 flex-shrink-0"/>}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Buy form */}
        <Card className="bg-white/5 border-white/10 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-green-400"/>
              Покупка
            </CardTitle>
            <CardDescription className="text-purple-200">
              Выбери токен слева, кошельки ниже, сумму (≥ 1 TON) и жми «Купить».
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-xs text-purple-200">Выбранный токен</label>
              <div className="mt-1 p-3 rounded bg-white/5 border border-white/10 text-white min-h-[44px]">
                {selectedToken ? (
                  <div>
                    <span className="font-medium">{selectedToken.symbol}</span>{' '}
                    <span className="text-purple-300 text-sm">({selectedToken.name})</span>
                    <div className="text-xs text-purple-300/80 mt-1">{shortAddr(selectedToken.master_address)}</div>
                  </div>
                ) : (
                  <span className="text-purple-200/60">— не выбран —</span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-purple-200">Сумма на кошелёк, TON</label>
                <Input
                  type="number"
                  min="1"
                  step="0.1"
                  value={amountTon}
                  onChange={(e) => setAmountTon(e.target.value)}
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-purple-200">Slippage, %</label>
                <Input
                  type="number"
                  min="0.3"
                  step="0.1"
                  value={slippage}
                  onChange={(e) => setSlippage(e.target.value)}
                  className="bg-white/10 border-white/20 text-white"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-purple-200">
                  Кошельки ({selectedWalletIds.size} выбрано из {walletsWithBalance.length} с балансом)
                </label>
                <Button variant="ghost" size="sm"
                        onClick={selectAllWalletsWithBalance}
                        className="text-purple-200 hover:text-white">
                  Все с балансом ≥ {(parseFloat(amountTon) || 1).toFixed(1)}+0.15 TON
                </Button>
              </div>
              <div className="max-h-[260px] overflow-y-auto space-y-1 pr-1">
                {walletsLoading ? (
                  <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin text-purple-400 mx-auto"/></div>
                ) : walletsWithBalance.length === 0 ? (
                  <div className="text-center py-6 text-purple-200/60">Нет кошельков с балансом.</div>
                ) : walletsWithBalance.map((w) => {
                  const id = w._id;
                  const active = selectedWalletIds.has(id);
                  return (
                    <label
                      key={id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        active ? 'bg-green-500/15 border border-green-400/50' : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <Checkbox checked={active} onCheckedChange={() => toggleWallet(id)} />
                      <span className="text-white text-sm flex-1 truncate">{shortAddr(w.address || id)}</span>
                      <Badge variant="outline" className="border-white/30 text-white">
                        {w._balance.toFixed(2)} TON
                      </Badge>
                    </label>
                  );
                })}
              </div>
            </div>

            <Button
              onClick={executeBuy}
              disabled={buying || !selectedToken || selectedWalletIds.size === 0}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {buying ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <ShoppingCart className="h-4 w-4 mr-2"/>}
              Купить за {((parseFloat(amountTon) || 0) * selectedWalletIds.size).toFixed(2)} TON
              {selectedWalletIds.size > 0 ? ` (${selectedWalletIds.size}×${amountTon} TON)` : ''}
            </Button>

            {lastBuyResult && (
              <div className="mt-3 text-xs space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <StatBox label="Всего" value={lastBuyResult.total_wallets ?? '—'} />
                  <StatBox label="Успешно" value={lastBuyResult.successful_buys ?? 0}
                           ok={(lastBuyResult.successful_buys ?? 0) > 0}/>
                  <StatBox label="Ошибок" value={lastBuyResult.failed_buys ?? 0}
                           ok={(lastBuyResult.failed_buys ?? 0) === 0}/>
                </div>
                {Array.isArray(lastBuyResult.purchases) && lastBuyResult.purchases.length > 0 && (
                  <div className="p-2 bg-green-500/10 border border-green-400/30 rounded">
                    <div className="text-green-300 font-medium mb-1">Успешные покупки:</div>
                    {lastBuyResult.purchases.map((p, i) => (
                      <div key={i} className="text-green-200/80 truncate">
                        ✓ {shortAddr(p.wallet)} → tx {(p.tx_hash || '').slice(0, 18)}…
                      </div>
                    ))}
                  </div>
                )}
                {Array.isArray(lastBuyResult.errors) && lastBuyResult.errors.length > 0 && (
                  <div className="p-2 bg-red-500/10 border border-red-400/30 rounded max-h-[200px] overflow-y-auto">
                    <div className="text-red-300 font-medium mb-1">Ошибки:</div>
                    {lastBuyResult.errors.map((e, i) => (
                      <div key={i} className="text-red-200/80 break-words">
                        ✗ {e.wallet ? shortAddr(e.wallet) + ': ' : ''}{e.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatBox = ({ label, value, ok }) => (
  <div className="bg-white/5 border border-white/10 rounded-lg p-3">
    <div className="text-xs text-purple-300">{label}</div>
    <div className={`text-lg font-semibold ${ok === false ? 'text-red-300' : ok === true ? 'text-green-300' : 'text-white'}`}>
      {value}
    </div>
  </div>
);

export default BlumOnChainScanner;
